import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Alert, Box, Button, Chip, TextField, Typography } from '@mui/material';
import { useFamilyChild } from './FamilyShell';
import { parentApi } from './parentApi';

const CHIPS = [
  { id: 'whenever', label: 'Whenever you can', reply: 'Usually within a few days' },
  { id: 'this_week', label: 'This week', reply: 'Usually within a day' },
  { id: 'today', label: 'Today', reply: 'Usually later today' },
  { id: 'urgent', label: 'Urgent — safety or wellbeing', reply: 'We’ll look at this as soon as we can' },
];

export default function FamilyCompose() {
  const { child } = useFamilyChild();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const channel = params.get('channel') === 'rm' ? 'rm' : 'teacher';
  const threadType = params.get('threadType') === 'complaint' ? 'complaint' : 'message';
  const batchId = params.get('batchId') || '';
  const contacts = useQuery(
    ['familyContacts', child.studentId],
    async () => (await parentApi.contacts(child.studentId)).data?.data,
    { enabled: !!child.studentId }
  );
  const c = contacts.data || {};
  const [priority, setPriority] = useState('this_week');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [file, setFile] = useState(null);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const who = threadType === 'complaint' ? c.rmName : channel === 'rm' ? c.rmName : c.teacherName;

  const send = async () => {
    setErr('');
    if (!body.trim()) {
      setErr('Write a message.');
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('channel', channel);
      fd.append('threadType', threadType);
      fd.append('priority', priority);
      fd.append('subject', subject);
      fd.append('body', body);
      if (batchId) fd.append('batchId', batchId);
      if (file) fd.append('file', file);
      const res = await parentApi.createMessage(child.studentId, fd);
      const id = res.data?.data?.threadId;
      navigate(id ? `/family/messages/${id}` : '/family/messages');
    } catch (e) {
      setErr(e.response?.data?.message || 'Could not send.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6">
        {threadType === 'complaint' ? 'Raise a complaint' : `Message ${who || ''}`}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        To {who || (channel === 'rm' ? 'your coordinator' : 'the teacher')} · about {child.firstName}
      </Typography>
      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      {channel === 'teacher' && threadType !== 'complaint' && (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            {CHIPS.map((ch) => (
              <Chip key={ch.id} label={ch.label} color={priority === ch.id ? 'primary' : 'default'} onClick={() => setPriority(ch.id)} />
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            {CHIPS.find((c) => c.id === priority)?.reply}
          </Typography>
        </>
      )}
      {threadType === 'complaint' && (
        <TextField fullWidth label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} sx={{ mb: 2 }} />
      )}
      <TextField fullWidth multiline minRows={4} label="Message" value={body} onChange={(e) => setBody(e.target.value)} />
      <Button component="label" sx={{ mt: 1 }}>
        Attach
        <input hidden type="file" accept="image/*,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </Button>
      {file ? <Typography variant="caption">{file.name}</Typography> : null}
      <Box sx={{ mt: 2 }}>
        <Button variant="contained" onClick={send} disabled={saving}>
          Send about {child.firstName}
        </Button>
      </Box>
    </Box>
  );
}
